// ============================================================
// PREZENCE — Audit Log viewer (admin/HR, read-only)
// Lists security events from /api/audit with filters + CSV export.
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import './audit.css';

const CATS = [
  { v: '', label: 'All' },
  { v: 'auth', label: 'Auth' },
  { v: 'data_access', label: 'Data access' },
  { v: 'admin', label: 'Admin' },
  { v: 'data_change', label: 'Changes' },
  { v: 'export', label: 'Exports' },
  { v: 'security', label: 'Security' },
];
const catColor = c => ({ auth: '#3ba8ff', data_access: '#a78bfa', admin: '#63dcbc', data_change: '#f59e0b', export: '#22c55e', security: '#ef4444' }[c] || '#64809a');
const outcomeColor = o => ({ success: '#22c55e', failure: '#ef4444', denied: '#f59e0b' }[o] || '#64809a');
const fmtTime = t => new Date(t).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const pageSize = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, pageSize });
      if (category) params.set('category', category);
      const res = await api.get(`/audit?${params}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Failed to load audit log');
    } finally { setLoading(false); }
  }, [page, category]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      const res = await api.get(`/audit/export?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
      a.click(); URL.revokeObjectURL(url);
      toast.success('Audit log exported');
    } catch (e) { toast.error('Export failed'); }
  };

  const pages = Math.ceil(total / pageSize) || 1;

  return (
    <div className="au-root">
      <div className="au-header">
        <div>
          <h1 className="au-title">Audit Log</h1>
          <p className="au-sub">Tamper-proof record of security events · {total} entries</p>
        </div>
        <button className="au-export" onClick={exportCsv}>⬇ Export CSV</button>
      </div>

      <div className="au-filters">
        {CATS.map(c => (
          <button key={c.v} className={`au-chip ${category === c.v ? 'active' : ''}`}
            onClick={() => { setCategory(c.v); setPage(1); }}>{c.label}</button>
        ))}
      </div>

      <div className="au-panel">
        {loading ? (
          <div className="au-loading"><div className="spinner" /></div>
        ) : logs.length === 0 ? (
          <div className="au-empty">No audit entries for this filter yet.</div>
        ) : (
          <div className="au-table-wrap">
            <table className="au-table">
              <thead>
                <tr><th>Time</th><th>Actor</th><th>Action</th><th>Category</th><th>Target</th><th>Outcome</th><th>IP</th></tr>
              </thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l.id}>
                    <td className="au-time">{fmtTime(l.created_at)}</td>
                    <td>
                      <div className="au-actor">{l.actor_email || 'unknown'}</div>
                      <div className="au-actor-role">{l.actor_role || ''}</div>
                    </td>
                    <td className="au-action">{l.action}</td>
                    <td><span className="au-cat" style={{ color: catColor(l.category), borderColor: catColor(l.category) + '55' }}>{l.category}</span></td>
                    <td className="au-target">{l.target_type ? `${l.target_type}${l.target_id ? ` · ${String(l.target_id).slice(0, 8)}` : ''}` : '—'}</td>
                    <td><span style={{ color: outcomeColor(l.outcome), fontWeight: 700, fontSize: 12 }}>{l.outcome}</span></td>
                    <td className="au-ip">{l.ip_address || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pages > 1 && (
        <div className="au-pager">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span>Page {page} of {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
}
